import { useState, useCallback, useEffect, useRef } from 'react';
import { useAdmin } from '../context/AdminContext';

export function PromptPanel() {
  const { adminApp, prompt, account } = useAdmin();

  // Upload form state
  const [formKey, setFormKey] = useState('');
  const [formAccountId, setFormAccountId] = useState('');
  const [filterWorkspaceId, setFilterWorkspaceId] = useState('');
  const [filterParticipantId, setFilterParticipantId] = useState('');
  const [formWorkspaceId, setFormWorkspaceId] = useState(adminApp.workspaceId);
  const [formParticipantId, setFormParticipantId] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync workspace ID
  useEffect(() => {
    setFormWorkspaceId(adminApp.workspaceId);
  }, [adminApp.workspaceId]);

  // Load prompts for account, optionally filtered by workspace
  useEffect(() => {
    if (formAccountId) {
      prompt.listPrompts(formAccountId, filterWorkspaceId || undefined, filterParticipantId || undefined);
    }
  }, [formAccountId, filterWorkspaceId, filterParticipantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = useCallback(async () => {
    if (!selectedFile || !formKey || !formAccountId) return;
    await prompt.uploadAndUpsertPrompt({
      file: selectedFile,
      key: formKey,
      accountId: formAccountId,
      ...(formWorkspaceId && { workspaceId: formWorkspaceId }),
      ...(formParticipantId && { participantId: formParticipantId }),
    });
    // Reset form on success
    if (!prompt.error) {
      setFormKey('');
      setSelectedFile(null);
      setFormParticipantId('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [selectedFile, formKey, formAccountId, formWorkspaceId, formParticipantId, prompt]);

  const handleDelete = useCallback(async (promptId: string) => {
    if (confirm(`Delete prompt ${promptId}?`)) {
      await prompt.deletePrompt(promptId);
      if (formAccountId) {
        await prompt.listPrompts(formAccountId, filterWorkspaceId || undefined, filterParticipantId || undefined);
      }
    }
  }, [prompt, formAccountId, formWorkspaceId]);

  const handleDownload = useCallback(async (promptId: string, key: string) => {
    await prompt.downloadPrompt(promptId, key);
  }, [prompt]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Prompts</h2>
        <div className="flex items-center gap-3">
          <select
            value={formAccountId}
            onChange={(e) => setFormAccountId(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 min-w-[280px]"
          >
            <option value="">Select account...</option>
            {account.accounts.entities.map((a) => (
              <option key={a.id} value={a.id}>{a.name} ({a.id.slice(0, 8)}...)</option>
            ))}
          </select>
          <input
            type="text"
            value={filterWorkspaceId}
            onChange={(e) => setFilterWorkspaceId(e.target.value)}
            placeholder="Workspace ID (empty = all)"
            className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 min-w-[280px]"
          />
          <input
            type="text"
            value={filterParticipantId}
            onChange={(e) => setFilterParticipantId(e.target.value)}
            placeholder="Participant ID (empty = all)"
            className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 min-w-[200px]"
          />
          <button
            onClick={() => formAccountId && prompt.listPrompts(formAccountId, filterWorkspaceId || undefined, filterParticipantId || undefined)}
            disabled={prompt.loading || !formAccountId}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {prompt.loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {prompt.error && (
        <div className="mt-2 text-sm text-red-500">{prompt.error}</div>
      )}

      {/* Prompt list table */}
      {prompt.prompts.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Key</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ref Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ref ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Scope</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Updated</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
              {prompt.prompts.map((p) => (
                <tr key={p.promptId}>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 font-mono">{p.key}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{p.refType}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 font-mono text-xs truncate max-w-48">{p.refId}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                    {p.participantId ? 'Participant' : p.workspaceId ? 'Workspace' : 'Account'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{new Date(p.updatedAt).toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleDownload(p.promptId, p.key)}
                        disabled={prompt.loading}
                        className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                      >
                        Download
                      </button>
                      <button
                        onClick={() => handleDelete(p.promptId)}
                        disabled={prompt.loading}
                        className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        !prompt.loading && (
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-12 text-center">
            <p className="text-gray-500 dark:text-gray-400">No prompts found. Upload one below.</p>
          </div>
        )
      )}

      {/* Upload form */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Upload Prompt</h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Key *
          </label>
          <input
            type="text"
            value={formKey}
            onChange={(e) => setFormKey(e.target.value)}
            placeholder="Prompt key (e.g. system-prompt)"
            className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            File *
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".md,.mdc,.markdown,.txt"
            onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
            className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 file:mr-4 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 dark:file:bg-blue-900 dark:file:text-blue-300 hover:file:bg-blue-100"
          />
          {selectedFile && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Workspace ID
          </label>
          <input
            type="text"
            value={formWorkspaceId}
            onChange={(e) => setFormWorkspaceId(e.target.value)}
            placeholder="Workspace ID"
            className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Participant ID
          </label>
          <input
            type="text"
            value={formParticipantId}
            onChange={(e) => setFormParticipantId(e.target.value)}
            placeholder="Participant ID (optional)"
            className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>

        <button
          onClick={handleUpload}
          disabled={prompt.loading || !formKey || !selectedFile || !formAccountId}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {prompt.loading ? 'Uploading...' : 'Upload & Save'}
        </button>
      </div>
    </div>
  );
}
